import { collection, doc, getFirestore, onSnapshot, query, where } from 'firebase/firestore';
import { useEffect, useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import useAuthContext from "../components/context/AuthContext";
import { SHOP } from '../entities/Shop';
import { TICKET, ticketConverter, TicketStatus } from '../entities/Ticket';
import firebase, { store } from '../firebase/Firebase';
import { toDate } from '../lib/datetime';
import { castTicketDate } from "../lib/ticket";
import { getClosedTicketsClosedBetween, getNotClosedTickets, getTicket, getTicketsByEmail } from "../repositories/ticketRepo";
import { closedTicketsState, notClosedTicketsState, ticketsByEmailState, ticketState } from "../state/selectors/ticketSelectors";
import ticketFieldListState from '../state/ticketFieldListState';
import ticketListState from "../state/ticketListState";
import { mergeUnique } from './../lib/array';
import { setHmsToZero } from './../lib/datetime';

export const useNotClosedTickets = (fields) => {

  const notClosedTickets = useRecoilValue(notClosedTicketsState)
  const setTickets = useSetRecoilState(ticketListState);
  const { currentShop } = useAuthContext();

  useEffect(() => {
    if (!currentShop || !fields?.length) return
    getNotClosedTickets(currentShop?.id).then((_tickets) => {
      const castedTickets = _tickets.map((ticket) => castTicketDate(ticket, fields))
      setTickets((__tickets) => {
        return mergeUnique(__tickets, castedTickets)
      })
    })
  }, [currentShop, fields])
  return notClosedTickets
}

export const useNotClosedTicketsSnapshot = (fields) => {
  const { currentShop } = useAuthContext();
  const setTickets = useSetRecoilState(ticketListState);
  useEffect(() => {
    if (!currentShop || !fields?.length) return

    const ref = doc(store, SHOP, currentShop?.id)
    const ticketRef = collection(ref, TICKET).withConverter(ticketConverter)
    const q = query(ticketRef, 
      where('ticketStatus','!=', TicketStatus.closed)
    )
    const unsub = onSnapshot(q, (snapshot) => {
      const _tickets = snapshot.docs.map((doc) => doc.data())
      const castedTickets = _tickets.map((ticket) => castTicketDate(ticket, fields))
      setTickets((_tickets) => {
        const merged = mergeUnique(_tickets, castedTickets)
        let sorted = merged.filter((_ticket) => {
          if (_ticket.ticketStatus != 'closed') {
            return true
          }

          const yesterday = new Date()
          setHmsToZero(yesterday)
          yesterday.setDate(yesterday.getDate()-1)
          const closedDate = toDate(_ticket.closedAt)
          return closedDate > yesterday
        });
        sorted.sort((a, b) => toDate(a.lastReceivedAt) - toDate(b.lastReceivedAt))
        return sorted
      })
    })

    return () => unsub()
  }, [currentShop, fields])
}

export const useClosedTicketsClosedBetween = (fields, start, end) => {

  const closedTickets = useRecoilValue(closedTicketsState);
  const setClosedTickets = useSetRecoilState(ticketListState)
  const { currentShop } = useAuthContext();
  useEffect(() => {
    if (!currentShop || !fields?.length || !start || !end) return
    getClosedTicketsClosedBetween(currentShop?.id, start, end).then((_tickets) => {
      const castedTickets = _tickets.map((ticket) => castTicketDate(ticket, fields))
      setClosedTickets((__tickets) => {
        return mergeUnique(__tickets, castedTickets)
      })
    })
  }, [currentShop, fields])
  return closedTickets
}

export const useTicket = (ticketId, fields) => {
  
  const ticket = useRecoilValue(ticketState(ticketId));
  const setTickets = useSetRecoilState(ticketListState);
  const { currentShop } = useAuthContext();

  useEffect(() => {
    if(!ticketId || !currentShop || !fields || !!ticket) return
    getTicket(currentShop?.id, ticketId).then((ticket) => {
      setTickets((ticketList) => {
        return mergeUnique(ticketList, [castTicketDate(ticket, fields)])
      })
    });
  }, [currentShop, ticketId, fields])

  return ticket
}

export const useTicketSnapshot = (id) => {
  const ticketFields = useRecoilValue(ticketFieldListState)
  const setTicketList = useSetRecoilState(ticketListState);
  const {currentShop} = useAuthContext();
  useEffect(() => {
    if (!currentShop?.id || !id) return
    const store = getFirestore(firebase)
    const shopRef = doc(store, SHOP, currentShop.id);
    const ticketRef = doc(shopRef, TICKET, id);
    onSnapshot(ticketRef, (doc) => {
      setTicketList((ticketList) => {
        const filtered = ticketList.filter((_ticket) => _ticket.id != id)
        return [...filtered, castTicketDate({
          ...doc.data(),
          id: id
        }, ticketFields)]
      })
    })
  }, [currentShop])
}

export const useCustomerTickets = (email, fields) => {
  
  const ticketsByEmail = useRecoilValue(ticketsByEmailState(email))
  const setTickets = useSetRecoilState(ticketListState)
  const { currentShop } = useAuthContext();
  useEffect(() => {
    if (!currentShop || !email || !fields?.length) return
    getTicketsByEmail(currentShop?.id, email).then((_tickets) => {
      setTickets((currentTickets) => {
        const castedTickets = currentTickets.map((ticket) => castTicketDate(ticket, fields))
        return mergeUnique(currentTickets, castedTickets);
      })
    })
  }, [currentShop, fields])
  return ticketsByEmail
}

export const useCustomerClosedTickets = (email, fields) => {
  
  const [ticketsByEmail, setTicketsByEmail] = useState([])
  const { currentShop } = useAuthContext();
  useEffect(() => {
    if (!currentShop || !email || !fields?.length) return
    getTicketsByEmail(currentShop?.id, email).then((_tickets) => {
      const castedTickets = _tickets.map((ticket) => castTicketDate(ticket, fields))
      setTicketsByEmail(mergeUnique(ticketsByEmail, castedTickets))
    })
  }, [currentShop, fields])
  return ticketsByEmail
}